import {
  clearMapFeatures,
  setTool,
  setTools,
  type Tool,
} from '@app/store/actions.js';
import { createReducer } from '@reduxjs/toolkit';
import type { LatLon } from '@shared/types/common.js';
import {
  type ElevationProvenance,
  elevationChartClose,
  elevationChartOpen,
  elevationChartSetActivePoint,
  elevationChartSetElevationProfile,
} from './actions.js';
import {
  type ElevationChartTarget,
  type ElevationChartTargetType,
  targetsEqual,
} from './target.js';

export interface ElevationProfilePoint extends LatLon {
  climbUp?: number;
  climbDown?: number;
  distance: number;
  ele: number;
}

// A waypoint paired onto the profile: its position on the distance axis and the
// elevation of the nearest track point, plus its name for the label.
export interface ElevationProfileWaypoint {
  distance: number;
  ele: number;
  label?: string;
}

export interface ElevationChartState {
  /**
   * What the chart is showing, or `null` when it is closed. The geometry itself
   * is never stored: `elevationChartProcessor` resolves this against current
   * state every time what it points at changes, which is what keeps the profile
   * in step with a re-route, a reshaped line or an arriving position.
   */
  target: ElevationChartTarget | null;
  activePoint: ElevationProfilePoint | null;
  elevationProfilePoints: Array<ElevationProfilePoint> | null;
  waypoints: ElevationProfileWaypoint[];
  /**
   * The terrain models the elevation API named for the shown profile, as source
   * tokens; empty when it named none, in which case the profile credits nobody.
   * Unrelated to `target`: these are what the numbers were read from, that is
   * what they describe.
   */
  sources: string[];
  /** What the shown profile's elevation is credited to; `null` when none is. */
  provenance: ElevationProvenance | null;
}

const initialState: ElevationChartState = {
  target: null,
  activePoint: null,
  elevationProfilePoints: null,
  waypoints: [],
  sources: [],
  provenance: null,
};

// The tool whose closing takes the chart with it. A drawn line is charted by id
// and outlives any tool, so it has none.
const targetTools: Record<ElevationChartTargetType, Tool | undefined> = {
  'route-planner': 'route-planner',
  'track-viewer': 'import-file',
  tracking: 'tracking',
  drawing: undefined,
};

export const elevationChartReducer = createReducer(initialState, (builder) =>
  builder
    // Re-aiming at what is already charted keeps the profile on screen; a new
    // target starts clean rather than showing the old line's elevation under
    // the new one's name.
    .addCase(elevationChartOpen, (state, { payload }) =>
      targetsEqual(state.target, payload.target)
        ? state
        : { ...initialState, target: payload.target },
    )
    .addCase(elevationChartSetActivePoint, (state, action) => {
      state.activePoint = action.payload;
    })
    .addCase(elevationChartSetElevationProfile, (state, action) => {
      state.elevationProfilePoints = action.payload.points;

      state.waypoints = action.payload.waypoints;

      state.sources = action.payload.sources;

      state.provenance = action.payload.provenance;
    })
    // Clear the chart when its own target's tool is closed (or all tools are),
    // not on every setTool — other tools open alongside it.
    .addCase(setTool, (state, action) =>
      action.payload.mode === 'close' &&
      state.target &&
      action.payload.tool === targetTools[state.target.type]
        ? initialState
        : state,
    )
    .addCase(setTools, (state, action) =>
      action.payload.length === 0 ? initialState : state,
    )
    .addCase(clearMapFeatures, setInitialState)
    .addCase(elevationChartClose, setInitialState),
);

function setInitialState() {
  return initialState;
}
